import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, TrendingDown, BarChart3, PieChart, Activity, DollarSign, Target, TrendingUpIcon, Calculator, Download, BarChart2, LineChart } from 'lucide-react';

const FeaturesSection = () => {
  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* CSS Keyframes for animations */}
      <style jsx>{`
        @keyframes growBar {
          from { height: 0px; }
          to { height: var(--final-height); }
        }
        
        @keyframes slideInUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes fadeInScale {
          from { 
            opacity: 0; 
            transform: scale(0.9); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
        
        @keyframes pulseGlow {
          0%, 100% { 
            box-shadow: 0 0 5px rgba(239, 68, 68, 0.5); 
          }
          50% { 
            box-shadow: 0 0 20px rgba(239, 68, 68, 0.8); 
          }
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 animate-[slideInUp_0.8s_ease-out]">
            Portfolio Management Made <span className="text-black">Simple</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-[slideInUp_0.8s_ease-out_0.2s_both]">
            Everything you need to track and analyze your stock investments
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Stock Portfolio Tracking */}
          <div className="flex flex-col gap-8 animate-[fadeInScale_0.8s_ease-out_0.4s_both]">
            {/* Feature Visual */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 min-h-[200px] flex items-center justify-center">
              {/* Stock Table Visualization */}
              <div className="w-full bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200 font-mono">
                <div className="grid grid-cols-4 gap-4 bg-gray-50 p-3 font-semibold text-gray-700 text-sm">
                  <span>Stock</span>
                  <span>Qty</span>
                  <span>Avg Price</span>
                  <span>P/L</span>
                </div>
                <div className="divide-y divide-gray-100">
                  <div className="grid grid-cols-4 gap-4 p-3 text-sm">
                    <span className="font-semibold text-gray-900">RELIANCE</span>
                    <span>100</span>
                    <span>₹2,450</span>
                    <span className="text-green-600 font-semibold flex items-center">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      +₹12,500
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 p-3 text-sm">
                    <span className="font-semibold text-gray-900">TCS</span>
                    <span>50</span>
                    <span>₹3,200</span>
                    <span className="text-red-600 font-semibold flex items-center">
                      <TrendingDown className="w-3 h-3 mr-1" />
                      -₹8,200
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature Content */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <BarChart3 className="w-6 h-6 mr-3 text-black" />
                Stock Portfolio Tracking
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Add buy/sell transactions with timestamps</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Automatic P&L calculation for sold stocks</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Track remaining quantities and watch prices</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Export data to CSV for external analysis</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Sector-wise Analytics */}
          <div className="flex flex-col gap-8 animate-[fadeInScale_0.8s_ease-out_0.6s_both]">
            {/* Feature Visual */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 min-h-[200px] flex items-center justify-center">
              {/* Sector Chart Visualization */}
              <div className="w-full h-40 flex items-end justify-between space-x-4">
                <div className="flex flex-col items-center space-y-3">
                  <div 
                    className="w-12 bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg shadow-lg transform transition-all duration-1000 ease-out hover:scale-110 hover:shadow-xl" 
                    style={{
                      height: '0px',
                      '--final-height': '80px',
                      animation: 'growBar 1.5s ease-out 0.2s forwards'
                    }}
                  ></div>
                  <span className="text-xs text-gray-700 font-semibold">IT</span>
                </div>
                <div className="flex flex-col items-center space-y-3">
                  <div 
                    className="w-12 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg shadow-lg transform transition-all duration-1000 ease-out hover:scale-110 hover:shadow-xl" 
                    style={{
                      height: '0px',
                      '--final-height': '50px',
                      animation: 'growBar 1.5s ease-out 0.4s forwards'
                    }}
                  ></div>
                  <span className="text-xs text-gray-700 font-semibold">Banking</span>
                </div>
                <div className="flex flex-col items-center space-y-3">
                  <div 
                    className="w-12 bg-gradient-to-t from-yellow-600 to-yellow-400 rounded-t-lg shadow-lg transform transition-all duration-1000 ease-out hover:scale-110 hover:shadow-xl" 
                    style={{
                      height: '0px',
                      '--final-height': '100px',
                      animation: 'growBar 1.5s ease-out 0.6s forwards'
                    }}
                  ></div>
                  <span className="text-xs text-gray-700 font-semibold">Energy</span>
                </div>
                <div className="flex flex-col items-center space-y-3">
                  <div 
                    className="w-12 bg-gradient-to-t from-red-600 to-red-400 rounded-t-lg shadow-lg transform transition-all duration-1000 ease-out hover:scale-110 hover:shadow-xl" 
                    style={{
                      height: '0px',
                      '--final-height': '40px',
                      animation: 'growBar 1.5s ease-out 0.8s forwards'
                    }}
                  ></div>
                  <span className="text-xs text-gray-700 font-semibold">Pharma</span>
                </div>
              </div>
            </div>
            
            {/* Feature Content */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <PieChart className="w-6 h-6 mr-3 text-black" />
                Sector-wise Analytics
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Organize stocks by market sectors</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>View sector-wise investment distribution</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Track performance across different sectors</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Identify your strongest performing sectors</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Live Price Monitoring */}
          <div className="flex flex-col gap-8 animate-[fadeInScale_0.8s_ease-out_0.8s_both]">
            {/* Feature Visual */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 min-h-[200px] flex items-center justify-center">
              {/* Live Price Card Visualization */}
              <div className="w-full bg-white rounded-xl p-5 shadow-lg border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-bold text-gray-900 text-lg animate-[slideInUp_0.6s_ease-out]">RELIANCE</span>
                  <div className="bg-red-500 text-white px-3 py-1 rounded-md text-xs font-semibold animate-[pulseGlow_2s_ease-in-out_infinite]">
                    LIVE
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="text-3xl font-bold text-gray-900 animate-[fadeInScale_0.8s_ease-out_0.3s_both]">₹2,562.50</div>
                  <div className="text-green-600 font-semibold flex items-center text-lg animate-[slideInUp_0.6s_ease-out_0.5s_both]">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    +₹12.50 (+0.49%)
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm text-gray-600 pt-2 border-t border-gray-200">
                    <div className="animate-[slideInUp_0.6s_ease-out_0.7s_both]">Open: ₹2,550</div>
                    <div className="animate-[slideInUp_0.6s_ease-out_0.8s_both]">High: ₹2,580</div>
                    <div className="animate-[slideInUp_0.6s_ease-out_0.9s_both]">Low: ₹2,545</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature Content */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Activity className="w-6 h-6 mr-3 text-black" />
                Live Price Monitoring
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Real-time stock price updates</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Live P&L calculation for holdings</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Price alerts and watch lists</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Market data integration</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Advanced Analytics */}
          <div className="flex flex-col gap-8 animate-[fadeInScale_0.8s_ease-out_1s_both]">
            {/* Feature Visual */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 min-h-[200px] flex items-center justify-center">
              {/* Analytics Dashboard Visualization */}
              <div className="w-full grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-md text-center animate-[fadeInScale_0.6s_ease-out_0.2s_both]">
                  <div className="text-xs text-gray-600 mb-2 font-medium">Total Investment</div>
                  <div className="text-xl font-bold text-gray-900 font-mono">₹5,25,000</div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-md text-center animate-[fadeInScale_0.6s_ease-out_0.4s_both]">
                  <div className="text-xs text-gray-600 mb-2 font-medium">Current Value</div>
                  <div className="text-xl font-bold text-gray-900 font-mono">₹5,89,500</div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-md text-center animate-[fadeInScale_0.6s_ease-out_0.6s_both]">
                  <div className="text-xs text-gray-600 mb-2 font-medium">Total P&L</div>
                  <div className="text-xl font-bold text-green-600 font-mono flex items-center justify-center">
                    <TrendingUpIcon className="w-4 h-4 mr-1" />
                    +₹64,500
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-md text-center animate-[fadeInScale_0.6s_ease-out_0.8s_both]">
                  <div className="text-xs text-gray-600 mb-2 font-medium">Return %</div>
                  <div className="text-xl font-bold text-green-600 font-mono">+12.3%</div>
                </div>
              </div>
            </div>
            
            {/* Feature Content */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Target className="w-6 h-6 mr-3 text-black" />
                Advanced Analytics
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Interactive charts and graphs</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Portfolio performance metrics</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Historical transaction analysis</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span>Performance comparison tools</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;